/**
 * Phase 3E-9j — Structured API-error normalizer.
 *
 * Backend error responses arrive in three distinct shapes:
 *   1. String        → `{detail: "Missing permission"}`
 *   2. Object        → `{detail: {code: "NO_PRICED_ROWS", message: "…"}}`
 *   3. Array (FastAPI validation) → `{detail: [{type,loc,msg,input,url}, …]}`
 *
 * Rendering shape (3) directly in JSX (via `toast.error(detail)`) throws
 * "Objects are not valid as a React child (found: object with keys
 * {type, loc, msg, input, url})" — the exact runtime crash reported on
 * Customer Profile save.
 *
 * `formatDetail` collapses any of the three shapes into a single
 * human-readable string safe to render anywhere. It is intentionally
 * pure and side-effect free so it can be called from an axios
 * interceptor without breaking downstream error handling.
 */
export function formatDetail(detail) {
  if (detail == null) return "";
  if (typeof detail === "string") return detail;
  if (typeof detail === "number" || typeof detail === "boolean") {
    return String(detail);
  }
  if (Array.isArray(detail)) {
    // FastAPI validation array. Turn every entry into "field: message".
    // Fall back to JSON if a shape we don't recognise slips in.
    const parts = detail.map((item) => {
      if (item == null) return "";
      if (typeof item === "string") return item;
      if (typeof item === "object") {
        const loc = Array.isArray(item.loc)
          ? item.loc.filter((x) => x !== "body").join(".")
          : "";
        const msg = item.msg || item.message || "";
        if (loc && msg) return `${loc}: ${msg}`;
        return msg || JSON.stringify(item);
      }
      return String(item);
    }).filter(Boolean);
    return parts.length ? parts.join(" · ") : "";
  }
  if (typeof detail === "object") {
    // Object error. Prefer `message` / `msg` / `code` fields.
    if (typeof detail.message === "string") return detail.message;
    if (typeof detail.msg === "string") return detail.msg;
    if (typeof detail.code === "string") return detail.code;
    try {
      return JSON.stringify(detail);
    } catch {
      return "";
    }
  }
  return "";
}

/**
 * Extract a user-safe error string from an axios error, without
 * throwing. Handles: HTTP body `detail`, network aborts, and generic
 * `error.message` fallback.
 */
export function formatApiError(error, fallback = "Something went wrong") {
  if (!error) return fallback;
  const detail = error?.response?.data?.detail;
  const asString = formatDetail(detail);
  if (asString) return asString;
  if (error.message) return error.message;
  return fallback;
}
